import { Controller } from 'stimulus';
import { FetchRequest } from '@rails/request.js';
import { validateBillable, saveBillable } from '@/js/modules/billable-utils';
import { modalContainer, openModal } from '@/js/modules/turbo-modal';

const GROUP_MODAL_TRIGGERS =
  '[data-action*="billable-form#createNewGroup"], [data-action*="billable-form#editGroup"]';
const DUPLICATE_TRIGGERS = '[data-action*="billable-form#duplicateItem"]';
const DUPLICATE_ERROR_MESSAGE = 'The item could not be duplicated. Please reload and try again.';

export default class extends Controller {
  static targets = ['itemsGrid', 'sendClientButton', 'sectionTemplate', 'pendingSection'];
  static values = { billableClearPath: String, groupItemsPath: String };
  static outlets = ['billable-tax'];
  #clientSelected = false;
  #createGroupBtn;
  #invalidClientPriceItems = new Set();
  #lastSavedFormState;
  #groupModalBusy = false;
  #duplicateBusy = false;
  #addSectionAfterSave = false;

  connect() {
    this.#createGroupBtn = this.element.querySelector('.group-selected-items');

    this.element.addEventListener(
      'billable-item:client-price-validity-changed',
      this.#clientPriceValidityChanged
    );
    this.element.addEventListener('billable-section:create-failed', this.#sectionCreateFailed);
    window.addEventListener('beforeunload', this.#unloadHandler);
    document.addEventListener('ui--turbo-modal:after-close', this.#groupModalClosed);
    this.#updateItemCountState();
    this.#updateSavedFormState();
  }

  disconnect() {
    this.element.removeEventListener(
      'billable-item:client-price-validity-changed',
      this.#clientPriceValidityChanged
    );
    this.element.removeEventListener('billable-section:create-failed', this.#sectionCreateFailed);
    window.removeEventListener('beforeunload', this.#unloadHandler);
    document.removeEventListener('ui--turbo-modal:after-close', this.#groupModalClosed);
    this.#invalidClientPriceItems.clear();
  }

  toggleSelection(event) {
    event.stopImmediatePropagation();

    if (this.#canCreateNewGroup()) {
      this.#createGroupBtn.classList.remove('hide');
    } else {
      this.#createGroupBtn.classList.add('hide');
    }
  }

  createNewGroup(event) {
    event.preventDefault();

    if (!this.#canCreateNewGroup()) {
      return;
    }

    const selectedItems = this.#selectedItemsForGrouping();
    const groupDisplay = this.#groupDisplayForSelectedItems();

    this.#saveAndOpenGroupModal(this.groupItemsPathValue, selectedItems, groupDisplay);
  }

  editGroup(event) {
    event.preventDefault();

    const primaryItem = event.currentTarget.closest('.billable-item');
    const selectedItems = [primaryItem, ...primaryItem.querySelectorAll('.billable-item')].map(
      (item) => item.dataset.id
    );
    const groupDisplay = primaryItem.querySelector('.billable-item__group-display').value;

    this.#saveAndOpenGroupModal(this.groupItemsPathValue, selectedItems, groupDisplay);
  }

  async #saveAndOpenGroupModal(groupEndpoint, selectedItems, groupDisplay) {
    if (this.#groupModalBusy || !this.element.reportValidity()) {
      return;
    }
    this.#setGroupModalBusy(true);

    let modalOpened = false;

    try {
      const { valid, errorMessage } = await validateBillable(this.element);
      if (!valid) {
        if (errorMessage) {
          window.alert(errorMessage);
        }
        return;
      }

      if (!(await saveBillable(this.element))) {
        return;
      }
      this.#updateSavedFormState();

      const body = new FormData();
      selectedItems.forEach((id) => body.append('item_ids[]', id));
      body.append('group_display', groupDisplay);

      const request = new FetchRequest('post', groupEndpoint, {
        body,
        responseKind: 'html',
      });
      const response = await request.perform();
      if (response.ok) {
        const html = await response.html;
        modalContainer().insertAdjacentHTML('beforeend', html);
        modalOpened = true;
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (!modalOpened) {
        this.#setGroupModalBusy(false);
      }
    }
  }

  #setGroupModalBusy(busy) {
    this.#groupModalBusy = busy;

    this.element.querySelectorAll(GROUP_MODAL_TRIGGERS).forEach((trigger) => {
      trigger.classList.toggle('button-disabled', busy);
      trigger.toggleAttribute('inert', busy);
    });
  }

  #groupModalClosed = (event) => {
    if (event.target.querySelector('.modal--group-items')) {
      this.#setGroupModalBusy(false);
    }
  };

  // A synchronous blur mid-gesture can crash Chrome's renderer).
  blurNumberField(event) {
    const field = event.target;
    if (field.matches('input[type="number"]') && field === document.activeElement) {
      event.preventDefault();
      setTimeout(() => field.blur());
    }
  }

  clientChanged(event) {
    const clients = event.detail?.clients || [];
    this.#clientSelected = clients.length > 0;
    this.#renderActionState();
  }

  saved() {
    this.#updateSavedFormState();
  }

  itemDeleted(event) {
    this.#invalidClientPriceItems.delete(event.detail.item);
    this.#renderActionState();
    this.#updateItemCountState();

    if (this.#activeItems().length > 0) {
      return;
    }

    if (this.#sections().length === 0) {
      this.element.querySelector('.billable-form-empty-message').classList.remove('hide');
      this.element.querySelector('.billable-form-controls').classList.add('hide');
    }

    const request = new FetchRequest('post', this.billableClearPathValue, {
      responseKind: 'json',
    });

    request
      .perform()
      .then((response) => {
        if (response.ok) {
          // The backend items are gone; drop their parked _destroy fields so a
          // later save doesn't reference destroyed records.
          this.element.querySelectorAll('.deleted-billable-item').forEach((el) => el.remove());
        }
      })
      .catch(console.error);
  }

  save(event) {
    event.preventDefault();
    this.#submitForm(event.currentTarget, event.params.submitAction);
  }

  addProducts(event) {
    event.preventDefault();
    const sectionField = this.element.querySelector('[name=billable_section_id]');
    if (sectionField) {
      sectionField.value = event.params.sectionId ?? '';
    }
    this.#submitForm(event.currentTarget, 'Save & Add Billable Product');
  }

  addSection(event) {
    event.preventDefault();

    const pending = this.itemsGridTarget.querySelector('.billable-section--pending');
    if (pending?.classList.contains('billable-section--saving')) {
      this.#addSectionAfterSave = true;
      return;
    }

    if (!pending) {
      this.#insertPendingSection();
    }

    this.#exitEmptyState();

    requestAnimationFrame(() => this.#revealPendingSection());
  }

  pendingSectionTargetDisconnected() {
    if (!this.#addSectionAfterSave) {
      return;
    }
    this.#addSectionAfterSave = false;

    if (!this.itemsGridTarget.querySelector('.billable-section--pending')) {
      this.#insertPendingSection();
    }

    requestAnimationFrame(() => this.#revealPendingSection());
  }

  addManualItem(event) {
    event.preventDefault();

    const link = event.currentTarget;

    const body = new FormData();
    body.append('tax', this.#taxAllChecked());

    const request = new FetchRequest('post', event.params.url ?? link.href, {
      body,
      responseKind: 'turbo-stream',
    });

    request.perform().catch(console.error);
  }

  duplicateItem(event) {
    event.preventDefault();

    const { url } = event.params;
    if (this.#duplicateBusy || !this.element.reportValidity()) {
      return;
    }
    this.#setDuplicateBusy(true);

    validateBillable(this.element)
      .then(({ valid, errorMessage }) => {
        if (!valid) {
          if (errorMessage) {
            window.alert(errorMessage);
          }
          return null;
        }

        return this.#saveAndDuplicate(url);
      })
      .catch((error) => {
        console.error(error);
        window.alert(DUPLICATE_ERROR_MESSAGE);
      })
      .finally(() => this.#setDuplicateBusy(false));
  }

  #saveAndDuplicate(url) {
    return saveBillable(this.element)
      .then((saved) => {
        if (!saved) {
          throw new Error('The billable could not be saved before duplicating an item');
        }
        this.#updateSavedFormState();

        return new FetchRequest('post', url, { responseKind: 'turbo-stream' }).perform();
      })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Item duplication failed with status ${response.statusCode}`);
        }
      });
  }

  #setDuplicateBusy(busy) {
    this.#duplicateBusy = busy;

    this.element.querySelectorAll(DUPLICATE_TRIGGERS).forEach((trigger) => {
      trigger.classList.toggle('button-disabled', busy);
      trigger.toggleAttribute('inert', busy);
    });
  }

  #taxAllChecked() {
    return this.hasBillableTaxOutlet ? this.billableTaxOutlet.allItemsTaxable : true;
  }

  async addPaintSwatch(event) {
    event.preventDefault();

    const link = event.currentTarget;
    link.classList.add('button-loading');
    const loader = this.#appendSwatchesLoader();
    return this.openBodyModal(link, { url: event.params.url ?? link.href }).finally(() => {
      link.classList.remove('button-loading');
      loader.remove();
    });
  }

  #appendSwatchesLoader() {
    const loader = document.createElement('div');
    loader.className = 'swatches-modal-loader';
    loader.innerHTML =
      '<i class="fa-regular fa-spinner-third fa-spin" aria-hidden="true"></i>Loading swatches';
    document.body.appendChild(loader);
    return loader;
  }

  openFlatRateServicesModal(event) {
    event.preventDefault();
    this.#openTurboModal(event.params.url ?? event.currentTarget.href);
  }

  async openPaymentModal(event) {
    event.preventDefault();

    await this.openBodyModal(event.currentTarget);
  }

  async openSendModal(event) {
    event.preventDefault();

    await this.openBodyModal(event.currentTarget);
  }

  // Validates and saves the billable first: submitting the fetched modal's form
  // navigates away, which would discard any unsaved changes on the billable form.
  // Also called through the billable-form outlet by controls outside the form.
  openBodyModal(trigger, { url = trigger.href } = {}) {
    if (trigger.hasAttribute('data-disabled') || !this.element.reportValidity()) {
      return Promise.resolve();
    }

    trigger.setAttribute('data-disabled', 'true');

    return validateBillable(this.element)
      .then(({ valid, errorMessage }) => {
        if (!valid) {
          if (errorMessage) {
            window.alert(errorMessage);
          }
          return false;
        }

        return saveBillable(this.element);
      })
      .then((saved) => {
        if (!saved) {
          return null;
        }

        this.#updateSavedFormState();

        if (trigger.dataset.turboStream === 'true') {
          return new FetchRequest('get', url, { responseKind: 'turbo-stream' }).perform();
        }

        return openModal(url);
      })
      .finally(() => trigger.removeAttribute('data-disabled'));
  }

  openTrackedHoursModal(event) {
    event.preventDefault();
    this.#openTurboModal(event.params.url ?? event.currentTarget.href);
  }

  itemsInserted(event) {
    const { container, lastRowBeforeInsert } = event.detail;

    this.#afterItemsInserted({ container, lastRowBeforeInsert });
  }

  #openTurboModal(url) {
    openModal(url).catch(console.error);
  }

  #afterItemsInserted({ container, lastRowBeforeInsert = null }) {
    this.dispatch('item-created', { target: this.itemsGridTarget });
    const section = container.closest('.billable-section');
    if (section) {
      this.dispatch('item-created', { target: section, prefix: 'billable-section' });
    }
    this.#updateItemCountState();
    this.#trackNewFormFieldsAsSaved();
    this.#exitEmptyState();

    setTimeout(() => this.#revealNewItem(lastRowBeforeInsert, container), 200);
    setTimeout(() => this.dispatch('changed', { prefix: 'billable-item' }));
  }

  #revealNewItem(lastItemBeforeInsert, container = this.itemsGridTarget) {
    const newItem = lastItemBeforeInsert
      ? lastItemBeforeInsert.nextElementSibling
      : container.querySelector(':scope > .billable-item');
    newItem?.querySelector('.field--multiline-billable-item')?.focus({ preventScroll: true });
    newItem?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }

  #sectionCreateFailed = () => {
    this.#addSectionAfterSave = false;
  };

  #insertPendingSection() {
    const shell = this.sectionTemplateTarget.content.firstElementChild.cloneNode(true);
    const unassigned = this.itemsGridTarget.querySelector('.billable-section--unassigned');
    if (unassigned) {
      unassigned.before(shell);
    } else {
      this.itemsGridTarget.append(shell);
    }
  }

  #exitEmptyState() {
    this.element.querySelector('.billable-form-empty-message')?.classList.add('hide');
    this.element.querySelector('.billable-form-controls')?.classList.remove('hide');
  }

  #revealPendingSection() {
    const section = this.itemsGridTarget.querySelector('.billable-section--pending');
    if (!section) {
      return;
    }

    section.querySelector('.billable-section__name-input')?.focus({ preventScroll: true });
    const rect = section.getBoundingClientRect();
    const top = window.scrollY + rect.top + (rect.height - window.innerHeight) / 2;
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  }

  #updateItemCountState() {
    this.itemsGridTarget.classList.toggle(
      'main-grid__body--multiple-items',
      this.#activeItems().length > 1
    );
  }

  async #validateBillableForm(callbacks = {}) {
    try {
      const { valid, errorMessage } = await validateBillable(this.element);
      if (valid) {
        callbacks.onSuccess?.();
      } else {
        callbacks.onError?.(errorMessage);
      }
    } catch {
      callbacks.onError?.('An unexpected error occurred');
    }
  }

  #submitForm(button, submitAction) {
    const form = this.element;
    if (button.hasAttribute('data-disabled') || !form.reportValidity()) {
      return;
    }

    form.querySelector('[name=submit_action]').value = submitAction;

    button.setAttribute('data-disabled', 'true');

    this.#validateBillableForm({
      onSuccess: () => {
        this.#updateSavedFormState();
        button.removeAttribute('data-disabled');
        const submitBtn = form.querySelector('[type="submit"]');
        submitBtn.removeAttribute('disabled');
        submitBtn.click();
      },
      onError: (errorMessage) => {
        alert(errorMessage);
        button.removeAttribute('data-disabled');
      },
    });
  }

  #activeItems() {
    return this.itemsGridTarget.querySelectorAll('.billable-item');
  }

  #sections() {
    return this.itemsGridTarget.querySelectorAll(
      '.billable-section:not(.billable-section--unassigned)'
    );
  }

  #clientPriceValidityChanged = (event) => {
    if (event.detail.valid) {
      this.#invalidClientPriceItems.delete(event.target);
    } else {
      this.#invalidClientPriceItems.add(event.target);
    }

    this.#renderActionState();
  };

  #renderActionState() {
    const invalidClientPrice = this.#invalidClientPriceItems.size > 0;

    this.element.querySelectorAll('[data-billable-actions-role="save"]').forEach((action) => {
      action.classList.toggle('button-disabled', invalidClientPrice);
      action.toggleAttribute('inert', invalidClientPrice);
    });

    if (this.hasSendClientButtonTarget) {
      this.sendClientButtonTarget.classList.toggle(
        'button-disabled',
        invalidClientPrice || !this.#clientSelected
      );
      this.sendClientButtonTarget.toggleAttribute('inert', invalidClientPrice);
    }
  }

  #canCreateNewGroup() {
    return this.element.querySelectorAll('.checkbox--group-billable-item:checked').length > 1;
  }

  #selectedItemsForGrouping() {
    const selectedItems = [];
    const checkboxes = this.element.querySelectorAll('.checkbox--group-billable-item:checked');
    checkboxes.forEach((checkbox) => {
      const item = checkbox.closest('.billable-item');
      const topLevelItem = item.parentElement.closest('.billable-item') ?? item;

      [topLevelItem, ...topLevelItem.querySelectorAll('.billable-item')].forEach((row) => {
        selectedItems.push(row.dataset.id);
      });
    });

    return selectedItems;
  }

  #groupDisplayForSelectedItems() {
    const selectedCheckbox = this.element.querySelector('.checkbox--group-billable-item:checked');
    if (!selectedCheckbox) {
      return '';
    }

    const item = selectedCheckbox.closest('.billable-item');
    return item.querySelector('.billable-item__group-display').value;
  }

  // There's no way to prevent page unloading or even surfacing a dialog during page uloading in Safari on iOS.
  // All the web API interfaces are present, and even the event is emitted but unloading machinery is
  // absent/nonfunctional.
  #unloadHandler = (event) => {
    if (this.#formChanged()) {
      event.preventDefault();
      event.returnValue = 'stay';
      return '';
    }
  };

  #formChanged() {
    const savedState = this.#lastSavedFormState;
    const currentState = this.#getFormState();

    return Object.entries(savedState).some(
      ([key, storedValue]) => currentState[key] !== storedValue
    );
  }

  #updateSavedFormState(state = this.#getFormState()) {
    this.#lastSavedFormState = state;
  }

  #trackNewFormFieldsAsSaved() {
    const savedState = this.#lastSavedFormState;
    const newFieldsState = Object.fromEntries(
      Object.entries(this.#getFormState()).filter(([key]) => !(key in savedState))
    );

    this.#updateSavedFormState({ ...savedState, ...newFieldsState });
  }

  #getFormState() {
    return (
      [...this.element.elements]
        .filter(({ type }) =>
          ['checkbox', 'number', 'select-one', 'text', 'textarea'].includes(type)
        )
        // These input values are loaded with selectize, which
        // loads them after the session storage data is saved.
        .filter(
          ({ id }) =>
            !['invoice_client_id', 'purchase_order_client_id', 'spec_sheet_client_id'].includes(id)
        )
        .filter(({ className }) => !className.includes('field--billable-item-total'))
        .filter(({ className }) => !className.includes('billable-section__name-input'))
        // Filter out identified selectize inputs.
        .filter(({ id }) => !id.includes('quote_client_id'))
        .filter(({ id }) => id !== 'invoice-tax-all')
        .reduce(
          (obj, { id, value, checked, type }) =>
            Object.assign(obj, { [id]: type === 'checkbox' ? checked : value }),
          {}
        )
    );
  }
}
